import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../../assets/icons/Bloggr.ai.svg';
import blogImg from '../../assets/images/write-blog.png';
import './verify-email.scss';

const OTP_LENGTH = 6;

function SignupVerifyEmail() {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail') || '';
  const token = localStorage.getItem('accessToken');

  useEffect(() => {
    if (countdown <= 0) { setCanResend(true); return; }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');
    if (value && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowLeft' && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!paste) return;
    const newOtp = [...otp];
    paste.split('').forEach((d, i) => { newOtp[i] = d; });
    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < OTP_LENGTH) { setError('Please enter all 6 digits.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/auth/verify-signup-otp`,
        { userId, otp: code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Email verified! Redirecting...');
      setTimeout(() => { window.location.href = `${process.env.REACT_APP_BASE_NAME}/onboarding`; }, 1500);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Invalid code. Please try again.');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResendLoading(true); setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/auth/resend-signup-otp`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCountdown(60); setCanResend(false);
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to resend code.');
    } finally { setResendLoading(false); }
  };

  const maskedEmail = userEmail
    ? userEmail.replace(/(.{2}).+(@.+)/, '$1***$2')
    : 'your email';

  return (
    <div className="auth-wrapper verify-otp-page">
      {/* Left panel — matches Login.tsx structure exactly */}
      <div className="left">
        <div className="content-wrapper verify-content">

          {/* Logo — top of the content column, self-aligned to the left */}
          <img
            className="logo"
            src={logo}
            alt="Bloggr.ai"
            onClick={() => { window.location.href = `${process.env.REACT_APP_URL}`; }}
            style={{ cursor: 'pointer', alignSelf: 'flex-start' }}
          />

          {/* Email icon ring */}
          <div className="otp-icon-ring">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff7c02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </div>

          <div className="top-section-details">
            <h2>Check your inbox 📬</h2>
            <p>
              We've sent a 6-digit code to<br />
              <span className="otp-email-highlight">{maskedEmail}</span>
            </p>
          </div>

          <div className="otp-spam-warning">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>Can't find the email? Please check your Spam folder.</span>
          </div>

          {/* OTP digit inputs */}
          <div className="otp-boxes" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`otp-input-${i}`}
                ref={(el) => { if (el) inputRefs.current[i] = el; }}
                className={`otp-box${digit ? ' otp-box--filled' : ''}${error ? ' otp-box--error' : ''}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                autoFocus={i === 0}
              />
            ))}
          </div>

          {error && <p className="otp-error">{error}</p>}
          {success && <p className="otp-success">{success}</p>}

          {/* Verify button */}
          <button
            id="verify-otp-btn"
            className="otp-verify-btn"
            onClick={handleVerify}
            disabled={loading || !!success}
          >
            {loading ? <span className="otp-spinner" /> : 'Verify Email'}
          </button>

          {/* Resend section */}
          <div className="otp-resend-row">
            <span className="otp-resend-text">Didn't receive it?</span>
            {canResend ? (
              <span
                className="otp-resend-link"
                onClick={!resendLoading ? handleResend : undefined}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResend(); }}
              >
                {resendLoading ? 'Sending...' : 'Resend code'}
              </span>
            ) : (
              <span className="otp-resend-timer">Resend in <strong>{countdown}s</strong></span>
            )}
          </div>

          <p className="otp-hint">Code expires in 10 minutes</p>
        </div>
      </div>

      {/* Right panel — same as Login */}
      <div className="right">
        <div className="auth-blog-content">
          <img className="blog-img" src={blogImg} alt="blog writing illustration" />
          <h3>Conquer writer's block once and for all with Bloggr.ai.</h3>
          <p>
            Effortlessly create SEO-optimized, original content of any type in minutes with Bloggr.ai.
            Automate your content marketing to streamline your workflow and focus on growing your business.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupVerifyEmail;
